import * as React from "react";
import _ from "lodash";

import styled from "styled-components";
import { getFocusBorder, FOCUS_CONFIG, FOCUS_BORDER } from "../../config/focusStyles";
import {
  FocusContext,
  useFocusable,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import { MoviesSlider } from "./moviesList";
import httpService from "../../services/http.service";
import { getCMSApiRoute } from "../../utils/api.utils";
import SearchFilters from "./searchFilters";
import useMockInput from "./input.mock";
import toast from "react-hot-toast";
import { Box, Typography } from "@mui/material";
import { IoClose } from "react-icons/io5";
import { useDebounceValue } from "usehooks-ts";
import { getAgeRangePublicRoute } from "../../utils/ageRange.util";
import { useNavigate } from "react-router-dom";
import { FONT_FAMILY, GLOBAL_COLORS } from "../../config/globalStyles";
import { PROFILE_COLORS, PROFILE_RADIUS, PROFILE_SPACING } from "../profile/styles/profileStyles";
import smoothScroll from "../../utils/animateScroll";
import { useInputMode } from "../../utils/hooks/useInputMode";

const PageContainer = styled.div`
  width: 95%;
  margin-right: auto;
  margin-left: auto;
  position: relative;
  height: 100vh;
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
  padding-top: 5rem;
`;

const ScrollingRows = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: calc(80vh - 200px);
  flex-shrink: 1;
  flex-grow: 1;
`;

const KeyboardCard = styled.div`
  background: linear-gradient(
    135deg,
    ${PROFILE_COLORS.cardGradientStart} 0%,
    ${PROFILE_COLORS.cardGradientEnd} 100%
  );
  border-radius: ${PROFILE_RADIUS.lg};
  border: 1px solid ${PROFILE_COLORS.border};
  padding: ${PROFILE_SPACING.lg};
  margin: 0.5rem auto;
  width: 100%;
  height: 256px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  direction: rtl;
`;

const KeyboardRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const KeyContainer = styled.div`
  border-radius: 50%;
  padding: 15px;
  color: #ffffff;
  font-size: 32px;
  font-weight: 900;
  height: 64px;
  width: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  position: relative;
  cursor: pointer;
  z-index: ${props => props.$focused ? "10" : "1"};
`;

const KeyBorderOverlay = styled.div`
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  border-radius: 50%;
  border: ${props => props.$focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive};
  background-color: ${props => props.$focused ? "#484848" : "transparent"};
  pointer-events: none;
  z-index: 1;
`;

const KeyText = styled.span`
  font-family: ${props => props.$isEng ? "Arial" : "AnjomanMax"};
  font-weight: 900;
  font-size: 40px;
  z-index: ${props => props.$focused ? "10" : "1"};
`;

const NoResultsContainer = styled.div`
  background: #212121;
  padding: 20px;
  margin: 40px 0;
  border-radius: 12px;
`;

const NoResultsText = styled.span`
  color: white;
  font-size: 24px;
`;

const keyboardKeys = [
  [
    "ا", "ب", "پ", "ت", "ث", "ج", "چ", "ح", "خ", "د",
    "ذ", "ر", "ز", "ژ", "س", "ش", "ص", "ض", "ط", "ظ",
    "ع", "غ", "ف", "ق", "ک", "گ", "ل", "م", "ن", "و", "ه", "ی",
  ],
  [
    "a", "b", "c", "d", "e", "f", "g", "h", "i", "j",
    "k", "l", "m", "n", "o", "p", "q", "r", "s", "t",
    "u", "v", "w", "x", "y", "z", "del", "space", "caps",
  ],
  ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"],
];

const KeyItem = ({ char, isEng, isCaps, onEnterPress }) => {
  const { ref, focused, focusSelf } = useFocusable({
    onEnterPress,
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = React.useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  const controllsGuide = {
    right: (
      <img
        src={"/images/arrow_right.svg"}
        width={64}
        height={64}
        alt={"To The right"}
      />
    ),
    left: (
      <img
        src={"/images/arrow_left.svg"}
        width={64}
        height={64}
        alt={"To The left"}
      />
    ),
    del: (
      <img
        src={"/images/del_button.svg"}
        width={38}
        height={29.25}
        alt={"Delete"}
        className="min-w-5 z-10"
      />
    ),
    sub: (
      <img
        src={"/images/sub_button.svg"}
        width={38}
        height={38}
        alt={"Submit"}
      />
    ),
    caps: isCaps ? (
      <img
        src={"/images/caps_filled.svg"}
        width={38}
        height={38}
        alt={"Caps On"}
        className="min-w-5 z-10"
      />
    ) : (
      <img
        src={"/images/caps.svg"}
        width={38}
        height={38}
        alt={"Caps Off"}
        className="min-w-5 z-10"
      />
    ),
    space: (
      <img
        src={"/images/space.svg"}
        alt="Space"
        width={38}
        className="min-w-5 z-10"
      />
    ),
  };

  return (
    <KeyContainer
      ref={ref}
      onClick={onEnterPress}
      onMouseEnter={handleMouseEnter}
      $focused={focused}
    >
      <KeyBorderOverlay $focused={focused} />
      {controllsGuide[char] ? (
        controllsGuide[char]
      ) : (
        <KeyText $isEng={isEng} $focused={focused}>
          {isCaps && !controllsGuide[char] ? char.toUpperCase() : char}
        </KeyText>
      )}
    </KeyContainer>
  );
};

const SearchKeyboard = ({
  submitSearch,
  addCharacter,
  removeCharacter,
  moveCursor,
}) => {
  const [isCaps, setCaps] = React.useState(false);
  const { ref, focusKey } = useFocusable({});

  return (
    <FocusContext.Provider value={focusKey}>
      <KeyboardCard ref={ref}>
        {keyboardKeys?.map((item, index) => (
          <KeyboardRow key={"row_" + index}>
            {item?.map((keyItem, keyIndex) => (
              <KeyItem
                char={keyItem}
                isCaps={isCaps}
                isEng={index === 1}
                key={"row_" + index + "_key_" + keyIndex}
                onEnterPress={() => {
                  if (keyItem === "space") {
                    addCharacter(" ");
                  } else if (keyItem === "right") {
                    moveCursor("left");
                  } else if (keyItem === "del") {
                    removeCharacter();
                  } else if (keyItem === "sub") {
                    submitSearch();
                  } else if (keyItem === "caps") {
                    setCaps(!isCaps);
                  } else {
                    addCharacter(
                      isCaps ? keyItem.toLocaleUpperCase() : keyItem
                    );
                  }
                }}
              />
            ))}
          </KeyboardRow>
        ))}
      </KeyboardCard>
    </FocusContext.Provider>
  );
};

const PAGE_SIZE = 30;

const SearchPageContent = () => {
  const ageRange = getAgeRangePublicRoute();
  const { ref, focusKey } = useFocusable();
  const [searchResult, setSearchResult] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(false);
  const [filters, setFilters] = React.useState({
    category: null,
    genre: null,
    country: null,
    kind: null,
    contentAgeRange: null,
    sortingType: null,
  });
  const [mountReset, setMountReset] = React.useState(false);
  const initRef = React.useRef(false);
  const scrollRef = React.useRef();
  const sentinelRef = React.useRef(null);
  const [requestInit, setRequestInit] = React.useState(false);

  const { InputComponent, moveCursor, addCharacter, removeCharacter, text } =
    useMockInput({
      initRef,
      isLoading,
      requestInit,
    });

  const [search, setSearch] = useDebounceValue(text, 1000);

  const submitSearch = async (nextPage = 1) => {
    if (isLoading) return;

    setIsLoading(true);

    const { category, genre, kind } = filters;
    const params = { pageNumber: nextPage, pageIndex: PAGE_SIZE, contentAgeRange: ageRange };
    if (text) params.title = text;
    if (category) params.category = category;
    if (genre) params.genre = genre;
    if (kind && kind !== "isTicketing" && kind !== "live") params.kind = kind;
    if (kind && kind === "isTicketing") params.isTicketing = true;
    if (kind && kind === "live") params.isLive = true;

    try {
      const response = await httpService.get(getCMSApiRoute("content"), {
        params,
      });

      if (response.status === 200) {
        const { data } = response.data;
        const newResults = data.results || [];

        if (nextPage === 1) {
          setSearchResult(newResults);
        } else {
          setSearchResult((prev) => _.uniqBy([...prev, ...newResults], (x) => x._id));
        }

        setPage(nextPage);
        setHasMore(data.total > nextPage * PAGE_SIZE);
      } else {
        toast.error("خطا در دریافت اطلاعات");
      }
    } catch (error) {
      Object.keys(error).map((item) => console.log(item));
      toast.error("خطا در دریافت اطلاعات");
    } finally {
      setIsDirty(true);
      setIsLoading(false);
    }
  };

  const loadMoreRef = React.useRef(null);
  loadMoreRef.current = () => {
    if (hasMore && !isLoading) {
      submitSearch(page + 1);
    }
  };

  const resetFilters = () => {
    setFilters({
      category: null,
      genre: null,
      country: null,
      kind: null,
      contentAgeRange: null,
      sortingType: null,
    });

    setPage(1);
    setHasMore(false);

    if (text?.length > 2) {
      submitSearch(1);
    } else {
      setSearchResult([]);
      setIsDirty(false);
    }
  };

  const { isMouseFocus } = useInputMode();
  const onRowFocus = React.useCallback(
    (props) => {
      if (isMouseFocus) return;
      const { y, height } = props;
      const targetScroll = y > height - 200 ? y - 100 : 0;
      smoothScroll(scrollRef, targetScroll, 500);

      // Pagination trigger via spatial navigation
      if (y >= height * 0.7) {
        loadMoreRef.current?.();
      }
    },
    [scrollRef, isMouseFocus]
  );

  // Primary: IntersectionObserver sentinel with 10vh rootMargin
  React.useEffect(() => {
    const scrollContainer = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!scrollContainer || !sentinel) return;

    const marginPx = Math.round(window.innerHeight * 0.1);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMoreRef.current?.();
        }
      },
      { root: scrollContainer, rootMargin: `0px 0px ${marginPx}px 0px`, threshold: 0.1 }
    );

    observer.observe(sentinel);

    return () => {
      observer.unobserve(sentinel);
    };
  }, [searchResult.length]);

  // Backup: scroll position check
  React.useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let scrollTimeout = null;
    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        loadMoreRef.current?.();
      }, 100);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [searchResult.length]);

  React.useEffect(() => {
    if (filters.category || filters.genre || filters.country || filters.kind) {
      setPage(1);
      setHasMore(false);
      submitSearch(1);
      setMountReset(true);
    } else {
      setTimeout(() => {
        setMountReset(false);
      }, 300);
    }
  }, [filters]);

  React.useEffect(() => {
    setSearch(text);
  }, [text]);

  React.useEffect(() => {
    if (search.length >= 3) {
      setPage(1);
      setHasMore(false);
      submitSearch(1);
    }
  }, [search]);

  React.useEffect(() => {
    setRequestInit(true);
  }, []);

  React.useEffect(() => {
    setTimeout(() => {
      setRequestInit(false);
    }, 250);
  }, [requestInit]);

  return (
    <FocusContext.Provider value={focusKey}>
      <PageContainer ref={ref}>
        <InputComponent />
        <SearchFilters
          onSearch={() => submitSearch()}
          resetFilters={resetFilters}
          mountReset={mountReset}
          filters={filters}
          setFilters={setFilters}
        />
        <SearchKeyboard
          addCharacter={addCharacter}
          removeCharacter={removeCharacter}
          moveCursor={moveCursor}
          submitSearch={() => submitSearch()}
        />
        {searchResult?.length > 0 && (
          <ScrollingRows ref={scrollRef}>
            <MoviesSlider
              movies={searchResult}
              onFocus={onRowFocus}
            />
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 56 }}>
                <span style={{ color: 'white', fontFamily: 'AnjomanMaxFN-Regular' }}>در حال بارگذاری...</span>
              </div>
            )}
          </ScrollingRows>
        )}
        {!isLoading && isDirty && searchResult?.length === 0 && (
          <NoResultsContainer>
            <NoResultsText>محتوایی یافت نشد</NoResultsText>
          </NoResultsContainer>
        )}
      </PageContainer>
    </FocusContext.Provider>
  );
};

export default SearchPageContent;
